import React, { useMemo } from 'react';
import { useOnboarding } from './OnboardingContext';
import { BiX, BiChevronRight, BiRocket, BiCheckShield } from 'react-icons/bi';
import QuickStartVideo from './QuickStartVideo';

const AdminWelcomeModal = () => {
    const { forceCloseWelcomeModal, snoozeWelcomeModal } = useOnboarding();

    // Dynamically fetch the logged-in user's data
    const user = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user') || '{}');
        } catch (e) {
            return {};
        }
    }, []);
    
    // 1. Dynamic First Name
    const firstName = user.first_name 
        ? user.first_name.charAt(0).toUpperCase() + user.first_name.slice(1) 
        : 'Admin';

    // 2. FULLY DYNAMIC AGENCY NAME (Checks for agency, organization, or company name)
    const agencyName = user.agency || user.organization_name || user.company_name;
    const subtitleMessage = agencyName 
        ? `Let's activate ${agencyName}'s account in less than 5 minutes.`
        : `Let's activate your account in less than 5 minutes.`;

    const handleContinue = () => {
        forceCloseWelcomeModal(true);
    };

    const handleSkip = () => {
        snoozeWelcomeModal();
    };

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop: Darkens background and allows dismissal on click */}
            <div 
                className="absolute inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity"
                onClick={handleSkip}
            ></div>

            {/* Modal Container: Optimized for mobile heights */}
            <div className="relative bg-white rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] flex flex-col transform animate-in fade-in zoom-in duration-300 overflow-hidden">
                
                {/* Header: Focused and Professional */}
                <div className="flex-shrink-0 p-6 sm:p-8 text-center bg-gradient-to-br from-blue-600 to-indigo-700 text-white relative">
                    <button 
                        onClick={handleSkip}
                        className="absolute top-4 right-4 p-1 rounded-full bg-white/10 text-white/80 hover:bg-white/20 hover:text-white transition-all focus:outline-none"
                        aria-label="Skip for now"
                    >
                        <BiX className="w-6 h-6 sm:w-8 sm:h-8" />
                    </button>
                    
                    <div className="inline-flex items-center justify-center p-3 sm:p-4 bg-white/20 rounded-2xl mb-3 sm:mb-4 backdrop-blur-md shadow-inner">
                        <BiRocket className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                    </div>
                    
                    <h2 className="text-2xl sm:text-3xl font-extrabold mb-1 sm:mb-2 tracking-tight">
                        Welcome, {firstName}!
                    </h2>
                    
                    {/* 👇 HERE IS YOUR DYNAMIC SUBTITLE 👇 */}
                    <p className="text-blue-100 text-base sm:text-lg font-medium px-4">
                        {subtitleMessage}
                    </p>
                </div>

                {/* Body: Highlighting the Guided Tour & Video (Scrollable on small screens) */}
                <div className="p-5 sm:p-8 space-y-5 sm:space-y-6 overflow-y-auto overscroll-contain">
                    <div className="space-y-4">
                        <div className="rounded-xl overflow-hidden shadow-sm border border-gray-100">
                            <QuickStartVideo />
                        </div>

                        <div className="pt-2 sm:pt-4 space-y-3">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-800 flex items-center">
                                <BiCheckShield className="text-green-500 mr-2 w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                                Your 14-Day Trial is Active
                            </h3>
                            <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                                Watch the quick walkthrough above or start the <b>Guided Workflow Tour</b> to see exactly how to create cases, manage clients, and assign field agents.
                            </p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-100 rounded-xl sm:rounded-2xl p-3 sm:p-4 flex items-start space-x-3 sm:space-x-4">
                            <div className="p-2 bg-blue-100/80 rounded-lg text-blue-600 mt-0.5 flex-shrink-0">
                                <BiRocket className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <div>
                                <p className="text-blue-800 font-semibold text-sm sm:text-base">The Dashboard Checklist</p>
                                <p className="text-blue-600 text-xs sm:text-sm mt-0.5 leading-snug">Follow the horizontal checklist at the top of your dashboard to complete your final setup steps.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer: Sticky at bottom, responsive button stacking */}
                <div className="flex-shrink-0 p-4 sm:p-6 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 border-t border-gray-100">
                    <button 
                        onClick={handleSkip}
                        className="text-gray-500 font-semibold hover:text-gray-800 transition-colors order-2 sm:order-1 text-sm sm:text-base py-2 px-4 w-full sm:w-auto text-center"
                    >
                        Skip for now
                    </button>
                    
                    <button 
                        onClick={handleContinue}
                        className="w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl sm:rounded-2xl shadow-lg shadow-blue-200 transition-all transform hover:-translate-y-0.5 flex items-center justify-center order-1 sm:order-2 text-base sm:text-lg group"
                    >
                        Start Guided Tour
                        <BiChevronRight className="ml-1 sm:ml-2 w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform" />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AdminWelcomeModal;